import { Option, Schema } from "effect";
import { schemaParser } from "./schema-compat.ts";
import { IdentityArrayInput, IdentityObjectInput } from "./identity-input.ts";
import {
  DataPropertyDescriptor, ObjectPrototype, OwnPropertyDescriptor,
  OwnPropertyDescriptors, ReferenceValue, RuntimeValueKind
} from "./property-inspection.ts";

import {
  redactOpaqueUrlsInText,
  redactedOpaqueUrlProtocol
} from "./url-redaction.ts";

export const TraceSpanStatus = /* @__PURE__ */ (() => Schema.Literals(["ok", "error"]))();
export type TraceSpanStatus = typeof TraceSpanStatus.Type;
export const TraceSpanKind = /* @__PURE__ */ (() => Schema.Literals(["request", "span", "gateway", "durable-object", "dynamic-worker"]))();
export type TraceSpanKind = typeof TraceSpanKind.Type;

export const TraceSpanRecord = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String.pipe(Schema.mutableKey),
  traceId: Schema.String.pipe(Schema.mutableKey),
  parentId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  workspaceId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  stationId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  component: Schema.String.pipe(Schema.mutableKey),
  operation: Schema.String.pipe(Schema.mutableKey),
  kind: TraceSpanKind.pipe(Schema.mutableKey),
  status: TraceSpanStatus.pipe(Schema.mutableKey),
  startedAt: Schema.Number.pipe(Schema.mutableKey),
  endedAt: Schema.Number.pipe(Schema.mutableKey),
  durationMs: Schema.Number.pipe(Schema.mutableKey),
  attributes: Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown)).pipe(Schema.mutableKey),
  input: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
  output: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
  error: Schema.Struct({
    type: Schema.String.pipe(Schema.mutableKey),
    message: Schema.String.pipe(Schema.mutableKey),
  }).pipe(Schema.optional, Schema.mutableKey),
}))();
export type TraceSpanRecord = typeof TraceSpanRecord.Type;

export const TraceSummary = /* @__PURE__ */ (() => Schema.Struct({
  traceId: Schema.String.pipe(Schema.mutableKey),
  workspaceId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  stationId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  rootOperation: Schema.String.pipe(Schema.mutableKey),
  startedAt: Schema.Number.pipe(Schema.mutableKey),
  endedAt: Schema.Number.pipe(Schema.mutableKey),
  durationMs: Schema.Number.pipe(Schema.mutableKey),
  status: TraceSpanStatus.pipe(Schema.mutableKey),
  spanCount: Schema.Number.pipe(Schema.mutableKey),
  truncated: Schema.Boolean.pipe(Schema.optional, Schema.mutableKey),
  errorCount: Schema.Number.pipe(Schema.mutableKey),
  components: Schema.Array(Schema.String).pipe(Schema.mutable, Schema.mutableKey),
}))();
export type TraceSummary = typeof TraceSummary.Type;

export const TraceStats = /* @__PURE__ */ (() => Schema.Struct({
  traceCount: Schema.Number.pipe(Schema.mutableKey),
  spanCount: Schema.Number.pipe(Schema.mutableKey),
  errorCount: Schema.Number.pipe(Schema.mutableKey),
  averageDurationMs: Schema.Number.pipe(Schema.mutableKey),
  p95DurationMs: Schema.Number.pipe(Schema.mutableKey),
  topOperations: Schema.Array(Schema.Struct({
    operation: Schema.String.pipe(Schema.mutableKey),
    count: Schema.Number.pipe(Schema.mutableKey),
  })).pipe(Schema.mutable, Schema.mutableKey),
}))();
export type TraceStats = typeof TraceStats.Type;

export const TraceListResult = /* @__PURE__ */ (() => Schema.Struct({
  traces: Schema.Array(TraceSummary).pipe(Schema.mutable, Schema.mutableKey),
  stats: TraceStats.pipe(Schema.mutableKey),
}))();
export type TraceListResult = typeof TraceListResult.Type;

const SENSITIVE_KEY =
  /(?:authorization|cookie|secret|token|password|credential|private[-_]?key|api[-_]?key|client[-_]?secret|query|path|preview|body|payload|params)/i;
const SECRET_VALUE =
  /(?:\b(?:Bearer|Basic)\s+\S+|\b(?:authorization|cookie|token|secret|password|credential|api[-_]?key|client[-_]?secret|access[-_]?key|private[-_]?key)\s*[:=]\s*\S+|-----BEGIN\s+(?:[A-Z]+\s+)?PRIVATE KEY-----|\beyJ[a-zA-Z0-9_-]{20,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\b)/i;
const HIGH_ENTROPY_VALUE = /\b(?:[a-f0-9]{32,}|[a-zA-Z0-9_-]{40,})\b/;
const SAFE_TRACE_ATTRIBUTE_KEY = /^[a-zA-Z0-9_.:/-]{1,160}$/;
const SAFE_TRACE_FIELD_NAME = /^[a-zA-Z0-9_.:/-]{1,160}$/;
const SAFE_CORRELATION_KEY =
  /^workerstation\.(?:change|task|run|parent_run|attempt|worktree|tree|input_tree|output_tree|candidate|check|review|namespace|trace_probe|mesh_request|worker_version)_id$/;
const SAFE_CONTENT_ADDRESS = /^sha256:[a-f0-9]{64}$/;
const SAFE_MESH_REQUEST_ID = /^[a-f0-9]{32}$/;
const SAFE_RUNTIME_NAMESPACE = /^wsn-[a-f0-9]{32,59}$/;
const SAFE_GENERATED_ID =
  /^(?:[A-Za-z][A-Za-z0-9._:@+-]{0,63}[-:@])?[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isString = /* @__PURE__ */ (() => Schema.is(Schema.String))();
const isNumber = /* @__PURE__ */ (() => Schema.is(Schema.Number))();
const isBoolean = /* @__PURE__ */ (() => Schema.is(Schema.Boolean))();
const isNumericOrBoolean = /* @__PURE__ */ (() => Schema.is(Schema.Union([Schema.Number, Schema.Boolean])))();
const isNullish = /* @__PURE__ */ (() => Schema.is(Schema.Union([Schema.Null, Schema.Undefined])))();
const isBigInt = /* @__PURE__ */ (() => Schema.is(Schema.BigInt))();
const isCallable = /* @__PURE__ */ (() => Schema.is(Schema.declare((value): value is Function => typeof value === "function")))();
const isObject = /* @__PURE__ */ (() => Schema.is(IdentityObjectInput))();
const isReference = /* @__PURE__ */ (() => Schema.is(ReferenceValue))();
const isArray = /* @__PURE__ */ (() => Schema.is(IdentityArrayInput))();
const isHeaders = /* @__PURE__ */ (() => Schema.is(Schema.instanceOf(Headers)))();
const isResponse = /* @__PURE__ */ (() => Schema.is(Schema.instanceOf(Response)))();
const isBinary = /* @__PURE__ */ (() => Schema.is(Schema.Union([
  Schema.instanceOf(ArrayBuffer),
  Schema.declare((value): value is ArrayBufferView => ArrayBuffer.isView(value))
])))();
const runtimeKind = /* @__PURE__ */ (() => schemaParser(RuntimeValueKind))();
const propertyDescriptors = /* @__PURE__ */ (() => schemaParser(OwnPropertyDescriptors))();
const propertyDescriptor = /* @__PURE__ */ (() => schemaParser(OwnPropertyDescriptor))();
const objectPrototype = /* @__PURE__ */ (() => schemaParser(ObjectPrototype))();
const dataDescriptor = (value: unknown) => Option.getOrUndefined(Schema.decodeUnknownOption(DataPropertyDescriptor)(value));
const isEnumerableDescriptor = /* @__PURE__ */ (() => Schema.is(Schema.Struct({ enumerable: Schema.Literal(true) })))();
const arrayLengthDescriptor = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.Struct({
  value: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))
})))();
const SafeErrorType = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[a-zA-Z][a-zA-Z0-9_$]{0,79}$/)))();
const SafeObjectName = /* @__PURE__ */ (() => SafeErrorType.check(Schema.makeFilter(value =>
  !SECRET_VALUE.test(value) && !HIGH_ENTROPY_VALUE.test(value)
)))();
const objectNameDescriptor = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.Struct({ value: SafeObjectName })))();
const functionNameDescriptor = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.Struct({
  value: Schema.String.check(Schema.isPattern(/^[a-zA-Z_$][a-zA-Z0-9_$]{0,79}$/),
    Schema.makeFilter(value => !SECRET_VALUE.test(value) && !HIGH_ENTROPY_VALUE.test(value)))
})))();
const sanitizedStringResult = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.Struct({ value: Schema.String })))();

function isSafeCorrelationValue(key: string | undefined, value: string): boolean {
  if (
    key === "build.compiler_result_digest" ||
    key === "build.compiler_generation" ||
    key === "run.compiler_revision_digest" ||
    key === "run.compiler_options_digest"
  ) {
    return SAFE_CONTENT_ADDRESS.test(value);
  }
  if (!key || !SAFE_CORRELATION_KEY.test(key)) return false;
  if (
    key === "workerstation.tree_id" ||
    key === "workerstation.input_tree_id" ||
    key === "workerstation.output_tree_id" ||
    key === "workerstation.candidate_id"
  ) {
    return SAFE_CONTENT_ADDRESS.test(value);
  }
  if (key === "workerstation.namespace_id") {
    return SAFE_RUNTIME_NAMESPACE.test(value);
  }
  if (key === "workerstation.mesh_request_id") {
    return SAFE_MESH_REQUEST_ID.test(value);
  }
  return SAFE_GENERATED_ID.test(value);
}

function safeObjectType(value: object): string {
  try {
    const prototype = objectPrototype(value);
    if (prototype === null) return "object";
    const constructor = dataDescriptor(propertyDescriptor([prototype, "constructor"]));
    if (!constructor) return "object";
    const name = propertyDescriptor([constructor.value, "name"]);
    return Option.getOrUndefined(objectNameDescriptor(name))?.value ?? "object";
  } catch {
    return "object";
  }
}

function ownDescriptors(value: object): Record<PropertyKey, unknown> | null {
  try {
    return propertyDescriptors(value);
  } catch {
    return null;
  }
}

function dataProperty(value: object, key: string): unknown {
  let current: object | null = value;
  for (let depth = 0; current && depth < 8; depth += 1) {
    try {
      const descriptor = propertyDescriptor([current, key]);
      if (descriptor !== undefined) return dataDescriptor(descriptor)?.value;
      current = objectPrototype(current);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function traceFieldName(name: string, index: number): string {
  return SAFE_TRACE_FIELD_NAME.test(name) &&
    !SECRET_VALUE.test(name) &&
    !HIGH_ENTROPY_VALUE.test(name)
    ? name
    : `field_${index}`;
}

function isInstanceOf<Instance extends object>(
  value: object,
  constructor: abstract new (...args: never[]) => Instance
): value is Instance {
  try {
    return Schema.is(Schema.instanceOf(constructor))(value);
  } catch {
    return false;
  }
}

function sanitizedString(value: string, key?: string): unknown {
  if (
    (key && SENSITIVE_KEY.test(key)) ||
    ((key === "build.compiler_result_digest" ||
      key === "build.compiler_generation" ||
      key === "run.compiler_revision_digest" ||
      key === "run.compiler_options_digest") &&
      !SAFE_CONTENT_ADDRESS.test(value))
  ) {
    return { type: "string", length: value.length, value: "[REDACTED]" };
  }
  if (
    SECRET_VALUE.test(value) ||
    (HIGH_ENTROPY_VALUE.test(value) &&
      !isSafeCorrelationValue(key, value))
  ) {
    return { type: "string", length: value.length, value: "[REDACTED]" };
  }
  let safe = redactOpaqueUrlsInText(value);
  try {
    const url = new URL(safe);
    const opaque = redactedOpaqueUrlProtocol(url.protocol);
    if (opaque) {
      safe = opaque;
    } else {
      url.username = "";
      url.password = "";
      url.search = "";
      url.hash = "";
      safe = url.toString();
    }
  } catch {
    // Preserve ordinary strings after secret-pattern checks.
  }
  return {
    type: "string",
    length: value.length,
    value: safe.length > 300 ? `${safe.slice(0, 300)}…` : safe
  };
}

export function sanitizeTraceMessage(value: string): string {
  if (SECRET_VALUE.test(value) || HIGH_ENTROPY_VALUE.test(value)) {
    return "[REDACTED]";
  }
  return redactOpaqueUrlsInText(value)
    .replace(
      /\b(https?:\/\/)[^\s/:@]+:[^\s/@]+@/gi,
      "$1[REDACTED]@"
    )
    .replace(/(["'`])[^"'`\n]{1,300}\1/g, "$1[REDACTED]$1")
    .replace(/([?&][^=\s&]{1,80}=)[^&\s]+/g, "$1[REDACTED]")
    .slice(0, 1_000);
}

function traceErrorMessage(error: unknown): string {
  if (isString(error)) return error;
  if (isReference(error)) {
    const message = dataProperty(error, "message");
    return isString(message) ? message : "Error details unavailable.";
  }
  return isNullish(error)
    ? "Error details unavailable."
    : String(error);
}

/** Test an error message without exposing it or invoking user-defined accessors. */
export function traceErrorMessageMatches(
  error: unknown,
  pattern: RegExp
): boolean {
  const message = traceErrorMessage(error);
  if (message === "Error details unavailable.") return false;
  pattern.lastIndex = 0;
  const matches = Schema.is(Schema.String.check(Schema.makeFilter(value => pattern.test(value))))(message);
  pattern.lastIndex = 0;
  return matches;
}

/** Extract bounded error diagnostics without invoking user-defined accessors. */
export function sanitizeTraceError(error: unknown): {
  type: string;
  message: string;
} {
  const type = isReference(error) ? safeObjectType(error) : runtimeKind(error);
  const message = traceErrorMessage(error);
  const safeType = Option.getOrUndefined(Schema.decodeUnknownOption(SafeErrorType)(type)) ?? "Error";
  return {
    type: safeType,
    message: sanitizeTraceMessage(message)
  };
}

/** Persist only a bounded error category; nested exception text is not telemetry. */
export function sanitizeTraceSpanError(error: unknown): {
  type: string;
  message: string;
} {
  return {
    type: sanitizeTraceError(error).type,
    message: "Error details unavailable."
  };
}

/**
 * Sanitize a scalar before exporting it as a native Worker span attribute.
 * The automatic trace store sanitizes its own copy separately, but native
 * attributes may also be retained by Cloudflare or an OTLP destination.
 */
export function sanitizeTraceAttribute(
  key: string,
  value: string | number | boolean | undefined | null
): string | number | boolean | undefined {
  if (!SAFE_TRACE_ATTRIBUTE_KEY.test(key)) return undefined;
  if (value === undefined || value === null) return undefined;
  if (!isString(value)) return value;
  if (SENSITIVE_KEY.test(key)) return "[REDACTED]";
  if (/(?:^|\.)error\.message$/i.test(key)) {
    return sanitizeTraceMessage(value);
  }
  const sanitized = sanitizedString(value, key);
  return Option.getOrUndefined(sanitizedStringResult(sanitized))?.value ?? "[REDACTED]";
}

/**
 * Convert arbitrary gateway values into bounded, JSON-safe diagnostics.
 * Request bodies and header values are never read; likely credentials and
 * high-entropy values are redacted before persistence.
 */
export function sanitizeTraceValue(
  value: unknown,
  key?: string,
  depth = 0,
  seen = new WeakSet<object>(),
  budget = { remaining: 64 },
  includeStringValues = true
): unknown {
  budget.remaining -= 1;
  if (budget.remaining < 0) return { type: "budget-exhausted", truncated: true };
  if (isNullish(value)) return null;
  if (isString(value)) {
    return includeStringValues
      ? sanitizedString(value, key)
      : { type: "string", length: value.length, value: "[OMITTED]" };
  }
  if (isNumericOrBoolean(value)) return value;
  if (isBigInt(value)) {
    const rendered = String(value);
    return {
      type: "bigint",
      length: rendered.length,
      value:
        rendered.length > 300 ? `${rendered.slice(0, 300)}…` : rendered
    };
  }
  if (isCallable(value)) {
    const name = propertyDescriptor([value, "name"]);
    return {
      type: "function",
      name: Option.getOrUndefined(functionNameDescriptor(name))?.value ?? "anonymous"
    };
  }
  if (!isObject(value)) return { type: runtimeKind(value) };

  if (isInstanceOf(value, Request)) {
    try {
      const urlValue = Reflect.get(Request.prototype, "url", value);
      const method = Reflect.get(Request.prototype, "method", value);
      const headers = Reflect.get(Request.prototype, "headers", value);
      const bodyUsed = Reflect.get(Request.prototype, "bodyUsed", value);
      const url = new URL(String(urlValue));
      return {
        type: "Request",
        method:
          isString(method) ? method.slice(0, 32) : "unknown",
        origin: url.origin.slice(0, 300),
        path: {
          segments: url.pathname.split("/").filter(Boolean).length,
          trailingSlash: url.pathname.endsWith("/")
        },
        headerNames:
          isHeaders(headers)
            ? [...Headers.prototype.keys.call(headers)]
                .slice(0, 100)
                .map((name) => name.slice(0, 160))
                .sort()
            : [],
        bodyUsed: isBoolean(bodyUsed) ? bodyUsed : false
      };
    } catch {
      return { type: "Request", unavailable: true };
    }
  }
  if (isInstanceOf(value, Response)) {
    try {
      const status = Reflect.get(Response.prototype, "status", value);
      const statusText = Reflect.get(Response.prototype, "statusText", value);
      const headers = Reflect.get(Response.prototype, "headers", value);
      const bodyUsed = Reflect.get(Response.prototype, "bodyUsed", value);
      return {
        type: "Response",
        status: isNumber(status) ? status : 0,
        statusText:
          isString(statusText) ? statusText.slice(0, 160) : "",
        headerNames:
          isHeaders(headers)
            ? [...Headers.prototype.keys.call(headers)]
                .slice(0, 100)
                .map((name) => name.slice(0, 160))
                .sort()
            : [],
        bodyUsed: isBoolean(bodyUsed) ? bodyUsed : false
      };
    } catch {
      return { type: "Response", unavailable: true };
    }
  }
  if (isInstanceOf(value, Error)) {
    const message = dataProperty(value, "message");
    return {
      type: safeObjectType(value),
      message: includeStringValues
        ? sanitizedString(
            isString(message) ? message : "Error details unavailable."
          )
        : {
            type: "string",
            length: isString(message) ? message.length : 0,
            value: "[OMITTED]"
          }
    };
  }
  if (isBinary(value)) {
    return {
      type: value.constructor.name,
      bytes: value.byteLength
    };
  }
  if (isInstanceOf(value, Date)) {
    try {
      return { type: "Date", value: Date.prototype.toISOString.call(value) };
    } catch {
      return { type: "Date", unavailable: true };
    }
  }
  if (isInstanceOf(value, URL)) {
    try {
      return sanitizedString(URL.prototype.toString.call(value), key);
    } catch {
      return { type: "URL", unavailable: true };
    }
  }
  if (seen.has(value)) return { type: "circular" };
  if (depth >= 4) {
    return { type: safeObjectType(value), truncated: true };
  }
  seen.add(value);

  if (isArray(value)) {
    const descriptors = ownDescriptors(value);
    if (!descriptors) return { type: "array", unavailable: true };
    const lengthDescriptor = descriptors.length;
    const length = Option.getOrUndefined(arrayLengthDescriptor(lengthDescriptor))?.value ?? 0;
    return {
      type: "array",
      length,
      values: Array.from({ length: Math.min(length, 20) }, (_, index) => {
        const descriptor = dataDescriptor(descriptors[index]);
        return descriptor
          ? sanitizeTraceValue(
              descriptor.value,
              undefined,
              depth + 1,
              seen,
              budget,
              includeStringValues
            )
          : { type: "accessor-or-empty", value: "[OMITTED]" };
      }),
      truncated: length > 20
    };
  }

  const descriptors = ownDescriptors(value);
  if (!descriptors) return { type: safeObjectType(value), unavailable: true };
  const names = Object.keys(descriptors).filter(name => isEnumerableDescriptor(descriptors[name]));
  const entries: Array<[string, unknown]> = [
    ["type", safeObjectType(value)]
  ];
  for (const [index, name] of names.slice(0, 30).entries()) {
    const descriptor = dataDescriptor(descriptors[name]);
    const safeName = traceFieldName(name, index);
    entries.push([
      safeName,
      descriptor
        ? sanitizeTraceValue(
            descriptor.value,
            name,
            depth + 1,
            seen,
            budget,
            includeStringValues
          )
        : { type: "accessor", value: "[OMITTED]" }
    ]);
  }
  if (names.length > 30) {
    entries.push(["truncatedKeys", names.length - 30]);
  }
  return Object.fromEntries(entries);
}

export function traceStatusForResult(
  status: TraceSpanStatus,
  output: unknown,
  attributes: Record<string, unknown>
): TraceSpanStatus {
  const responseStatus =
    isResponse(output)
      ? output.status
      : Object.entries(attributes).find(
          ([key, value]) =>
            key.endsWith("response.status_code") && isNumber(value)
        )?.[1];
  const reportedError = Object.entries(attributes).some(
    ([key, value]) =>
      (key.endsWith(".error") && value === true) ||
      (key === "span.status" && value === "error")
  );
  return status === "error" ||
    reportedError ||
    (isNumber(responseStatus) && responseStatus >= 400)
    ? "error"
    : "ok";
}

export function sanitizeTracePayload(value: unknown): unknown {
  return sanitizeTraceValue(
    value,
    undefined,
    0,
    new WeakSet<object>(),
    { remaining: 64 },
    false
  );
}

export function summarizeTraces(
  spans: TraceSpanRecord[],
  limit = 50
): TraceListResult {
  const groups = new Map<string, TraceSpanRecord[]>();
  for (const span of spans) {
    const group = groups.get(span.traceId) ?? [];
    group.push(span);
    groups.set(span.traceId, group);
  }

  const traces = [...groups.entries()]
    .map(([traceId, group]): TraceSummary => {
      group.sort((left, right) => left.startedAt - right.startedAt);
      const root = group.find((span) => !span.parentId) ?? group[0];
      const startedAt = Math.min(...group.map((span) => span.startedAt));
      const endedAt = Math.max(...group.map((span) => span.endedAt));
      const errorCount = group.filter((span) => span.status === "error").length;
      return {
        traceId,
        workspaceId: root.workspaceId ?? group.find((span) => span.workspaceId)?.workspaceId,
        stationId: root.stationId ?? group.find((span) => span.stationId)?.stationId,
        rootOperation: root.operation,
        startedAt,
        endedAt,
        durationMs: Math.max(0, endedAt - startedAt),
        status: errorCount > 0 ? "error" : "ok",
        spanCount: group.length,
        errorCount,
        components: [...new Set(group.map((span) => span.component))].sort()
      };
    })
    .sort((left, right) => right.startedAt - left.startedAt)
    .slice(0, Math.max(1, Math.min(limit, 100)));

  const durations = traces.map((trace) => trace.durationMs).sort((a, b) => a - b);
  const operationCounts = new Map<string, number>();
  for (const span of spans) {
    operationCounts.set(span.operation, (operationCounts.get(span.operation) ?? 0) + 1);
  }
  const topOperations = [...operationCounts.entries()]
    .map(([operation, count]) => ({ operation, count }))
    .sort((left, right) => right.count - left.count || left.operation.localeCompare(right.operation))
    .slice(0, 10);

  return {
    traces,
    stats: {
      traceCount: traces.length,
      spanCount: spans.length,
      errorCount: spans.filter((span) => span.status === "error").length,
      averageDurationMs: traces.length
        ? traces.reduce((sum, trace) => sum + trace.durationMs, 0) / traces.length
        : 0,
      p95DurationMs: durations.length
        ? durations[Math.min(durations.length - 1, Math.ceil(durations.length * 0.95) - 1)]
        : 0,
      topOperations
    }
  };
}
